import { Controller } from "@hotwired/stimulus"

// Entrance motion for the Lead Finder UPG panels: bars grow to their width,
// whole-number figures count up from zero, and items fade in one after the
// other. It runs when the panel lands, which for a lazy frame inside a shut
// section means when the rep opens it.
//
// The server renders the FINAL state: every bar at its width and every figure
// at its value. This controller only rewinds that state and plays it forward,
// so with JavaScript off, or with reduced motion requested, the rep sees the
// finished panel and nothing moves.
//
//   data-prospecting--reveal-target="bar"   style="width: 62%"
//   data-prospecting--reveal-target="count" data-value="41"   (text is "41")
//     add data-plain="true" for a count printed without thousands separators
//     (the stated won figure), so it never looks like money mid-animation.
//   data-prospecting--reveal-target="item"
export default class extends Controller {
  static targets = ["bar", "count", "item"]
  static values = { duration: { type: Number, default: 900 } }

  connect() {
    if (this.reducedMotion()) return

    this.bars = this.barTargets.map((bar) => {
      const width = bar.style.width
      bar.style.transition = "none"
      bar.style.width = "0%"
      return [bar, width]
    })
    this.itemTargets.forEach((item) => {
      item.style.opacity = "0"
      item.style.transform = "translateY(4px)"
    })

    // Two frames: the first commits the rewound state, the second plays it.
    this.frame = requestAnimationFrame(() => {
      this.frame = requestAnimationFrame(() => this.play())
    })
  }

  disconnect() {
    if (this.frame) cancelAnimationFrame(this.frame)
    if (this.countFrame) cancelAnimationFrame(this.countFrame)
  }

  play() {
    const ms = this.durationValue

    this.bars.forEach(([bar, width], index) => {
      bar.style.transition = `width ${ms}ms cubic-bezier(0.22, 1, 0.36, 1) ${index * 80}ms`
      bar.style.width = width
    })

    this.itemTargets.forEach((item, index) => {
      item.style.transition = `opacity 400ms ease-out ${index * 60}ms, transform 400ms ease-out ${index * 60}ms`
      item.style.opacity = ""
      item.style.transform = ""
    })

    this.countUp(ms)
  }

  // Only whole, non-negative numbers are counted. Anything else (a dash for an
  // unknown figure, a percentage with a decimal) keeps its server-rendered
  // text, so a count can never land on a number the server did not print.
  countUp(ms) {
    const counts = this.countTargets
      .map((el) => [
        el,
        Number.parseInt(el.dataset.value, 10),
        el.textContent,
        el.dataset.plain === "true",
      ])
      .filter(([, value]) => Number.isInteger(value) && value > 0)
    if (counts.length === 0) return

    const locale = document.documentElement.lang || undefined
    const grouped = new Intl.NumberFormat(locale)
    const plain = new Intl.NumberFormat(locale, { useGrouping: false })
    const start = performance.now()
    const tick = (now) => {
      const progress = Math.min((now - start) / ms, 1)
      const eased = 1 - (1 - progress) ** 3
      counts.forEach(([el, value, finalText, isPlain]) => {
        const format = isPlain ? plain : grouped
        el.textContent =
          progress < 1 ? format.format(Math.round(value * eased)) : finalText
      })
      if (progress < 1) this.countFrame = requestAnimationFrame(tick)
    }
    this.countFrame = requestAnimationFrame(tick)
  }

  reducedMotion() {
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    )
  }
}
